import Button from '@/components/core/Button';
import Select from '@/components/core/Select';
import { scheduleRoomsSubscription } from '@/graph/rooms';
import RouterLink from '@/se/components/RouterLink';
import { getNestedValue } from '@/se/utilities/data/object';
import responsive, { breakpoint } from '@/se/utilities/responsive';
import { ScheduleUserType } from '@/types/ScheduleUser';
import {
  getOperationRooms as getOperationRoomsOptimized,
  getProcedures as getProceduresOptimized,
  transformScheduleOperationRooms as transformScheduleOperationRoomsOptimized,
} from './schedule/transformOptimized.ts';
import { useQuery, useSubscription } from '@apollo/client';
import { Typography, useTheme } from '@material-ui/core';
import { AbsoluteSpinner } from '@/se/components/Spinner';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { ArrowBack } from '@material-ui/icons';
import clsx from 'clsx';
import { addDays, addMinutes, format, isAfter, parse, startOfDay } from 'date-fns';
import queryString from 'query-string';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Route } from 'react-router-dom';
import styled from 'styled-components';

import {
  scheduleProceduresOptimizedSubscription,
  dailyScheduleEntitiesQuery,
  proceduresDayOverSubscription,
} from '@/graph/procedures';
import { useDenormalizedProcedures } from '@/utils/procedureDenormalization';
import { useScheduleUpdates } from '@/hooks/useScheduleUpdates';
import { isWithinPollGracePeriod, isEventStale } from '@/hooks/useScheduleSubscription';

import ClientUpdater from '../../ClientUpdater';
import ToastBar from '../../core/ToastBar';
import CheckboxInput from '../../inputs/CheckboxInput';
import { Formats } from '../../inputs/upload/formats';
import { ScheduleUploadButton } from '../../inputs/upload/UploadButton';
import HospitalInfo from '../../kiosk/HospitalInfo';

import EmptySchedule from './EmptySchedule';
import SubscriptionErrorHandler from './schedule/SubscriptionErrorHandler';
import ProcedureEditor from './schedule/edit/ProcedureEditor';
import Filters from './schedule/Filters';
import MobileORNavigation from './schedule/MobileORNavigation';
import MonthlySchedule from './schedule/MonthlyView/MonthlySchedule';
import Overview from './schedule/overview/Overview';
import PDFScheduleButton from './schedule/pdf/PDFScheduleButton';
import { getRoomWithStaff, scheduleStaff } from './schedule/pdfGeneratorUtils';
import { ScheduleProcedureForm } from './schedule/procedure/ScheduleProcedureForm';
import ScheduleFooter from './schedule/ScheduleFooter';
import ScheduleNotificationDialog from './schedule/ScheduleNotificationDialog';
import SendNotificationDropdown from './schedule/SendNotificationDropdown';
import MultipleSelectionBox from './schedule/staff/MultipleSelectionBox';
import { MultipleStaffShiftSelectionContextProvider } from './schedule/staff/MultipleStaffShiftSelection';
import StaffingCostButton from './schedule/staff/StaffingCostButton';
import StaffShiftCopyButton from './schedule/staff/StaffShiftCopyButton';
import Timeline from './schedule/timeline/Timeline';
import ToggleOverlay from './schedule/ToggleOverlay';
import WeeklySchedule, { padWeek } from './schedule/WeekView/WeekSchedule';
import ScheduleListView from './schedule/listView/listView';
import { useClock } from '@/components/pages/kiosk/HospitalSchedule.hooks';
import { useStableSubscriptionData } from '@/hooks/useDeepMemo';

const Header = styled.div`
  flex: 0 0 3em;

  ${responsive.md.andSmaller`
    flex: 0 0 auto;
  `};
`;

export const EditingHeader = styled(Header)`
  padding: 0.875em 1em;
  display: flex;
  justify-content: stretch;
  align-items: center;

  ${responsive.md.andSmaller`
    overflow: hidden;
    flex-wrap: wrap;
    flex-flow: column;
  `};

  > div {
    overflow: hidden;
    flex: 1;
    display: flex;
    align-items: center;
  }
`;

export const Row = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;

  > div:first-of-type {
    margin-top: 0.75em;
    margin-bottom: 0;

    > label {
      align-items: center;
    }
  }
`;

export const HospitalName = styled.h3`
  opacity: 0.35;
  font-size: 1.125em;
  font-weight: 500;
  padding-bottom: 0.5em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  text-align: center;
  width: 100%;
`;

const MonthContainer = styled.div`
  text-align: center;
  margin-bottom: 1em;
  font-size: 1.125em;
  font-weight: 500;
  background-color: ${props => props.theme.backgroundColor.string()};
`;

export const formats = [Formats.CSV, Formats.XLS, Formats.XLSX, Formats.TXT, Formats.PDF];

const ScrollableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const shouldFilterOrWithoutId = hospitalId =>
  (window.location.hostname === 'view2.ospitek.com' && hospitalId === 24) ||
  (window.location.hostname === 'testing.ospitek.com' && hospitalId === 77);

const Schedule = props => {
  const {
    scope,
    isSuperAdmin,
    isGroupAdmin,
    isMonitoringEngineer,
    isPreOPNurse,
    isNursing,
    isAdmin,
    isFrontDesk,
    isOperationsManager,
    isAnesthesiologist,
    isScheduleUser,
    location,
    history,
    match,
    isKiosk,
    config,
    hasScheduleAccessAllRight,
    myProceduresChecked,
    setMyProceduresChecked,
    scheduleUserId,
    scheduleUserType,

    /** Unified from HospitalSchedule */
    parsedParams,
    physician,
    date,
    dayIsOver,
  } = props;

  const formattedDate = useMemo(() => format(date, 'YYYY-MM-DD'), [date]);

  const roomsSub = useSubscription(scheduleRoomsSubscription, {
    variables: { date: formattedDate },
    shouldResubscribe: true,
  });

  // Monitor subscription errors with useEffect (Apollo Client 4.0 compatible)
  useEffect(() => {
    if (roomsSub.error) {
      console.error('[HospitalSchedule] Rooms subscription error:', roomsSub.error.message, {
        date: formattedDate,
        timestamp: new Date().toISOString(),
      });
      // Apollo will automatically retry due to shouldResubscribe: true
    }
  }, [roomsSub.error, formattedDate]);

  const { data: entitiesData, loading: entitiesLoading } = useQuery(dailyScheduleEntitiesQuery, {
    variables: { date: formattedDate },
    fetchPolicy: 'cache-first', // Load from cache, fetch if not present
    nextFetchPolicy: 'cache-only', // After first load, never refetch - rely on subscriptions
  });

  // Reload page when patientNameFormat changes to re-establish session with new format
  const prevPatientNameFormat = useRef(config?.patientNameFormat);

  useEffect(() => {
    if (prevPatientNameFormat.current && prevPatientNameFormat.current !== config?.patientNameFormat) {
      window.location.reload();
    }
    prevPatientNameFormat.current = config?.patientNameFormat;
  }, [config?.patientNameFormat]);

  const {
    data: subscriptionData,
    loading: proceduresLoadingRaw,
    error: proceduresError,
  } = useSubscription(scheduleProceduresOptimizedSubscription, {
    variables: {
      date: formattedDate,
      physician: physician ? parseInt(physician, 10) : undefined,
    },
    shouldResubscribe: true,
  });

  const subscriptionProceduresData = subscriptionData?.scheduleUpdatedIncremental || emptyArray;

  // Track if we've ever received data - once we have data, don't show loading on reconnect
  const hasReceivedData = useRef(false);
  const loadingTimeoutRef = useRef(null);
  const [forceNotLoading, setForceNotLoading] = useState(false);

  // Mark that we've received data
  useEffect(() => {
    if (subscriptionProceduresData.length > 0) {
      hasReceivedData.current = true;
      setForceNotLoading(false); // Reset when we get fresh data
    }
  }, [subscriptionProceduresData]);

  // Loading timeout: If loading for >15s after having data before, force not loading
  // This prevents stuck loading state after WebSocket reconnection
  // For kiosk screens that never received data, force page reload after 60s
  const reloadTimeoutRef = useRef(null);

  useEffect(() => {
    if (proceduresLoadingRaw && hasReceivedData.current) {
      // Case 1: Had data before, now loading again (reconnection scenario)
      console.log('[HospitalSchedule] Subscription loading after reconnect, starting timeout');
      loadingTimeoutRef.current = setTimeout(() => {
        console.warn('[HospitalSchedule] Loading timeout reached after reconnect, forcing display with cached data');
        setForceNotLoading(true);
      }, 15000); // 15 second timeout
    } else if (proceduresLoadingRaw && !hasReceivedData.current && isKiosk) {
      // Case 2: Kiosk never received data - subscription may be stuck
      // Force page reload after 60s to recover from zombie WebSocket state
      console.log('[HospitalSchedule] Kiosk loading without prior data, starting reload timeout');
      reloadTimeoutRef.current = setTimeout(() => {
        console.error('[HospitalSchedule] Kiosk stuck in loading for 60s without data, forcing page reload');
        window.location.reload();
      }, 60000); // 60 second timeout for kiosk reload
    } else {
      // Clear timeouts if loading completes
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
        reloadTimeoutRef.current = null;
      }
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
      }
    };
  }, [proceduresLoadingRaw, isKiosk]);

  // Use forceNotLoading to override loading state after timeout
  // This allows the UI to display cached data instead of being stuck on loading
  const proceduresLoading = forceNotLoading ? false : proceduresLoadingRaw;

  useEffect(() => {
    if (proceduresError) {
      console.error('[HospitalSchedule] Procedures subscription error:', proceduresError.message);
    }
  }, [proceduresError]);

  const proceduresData = useDenormalizedProcedures(subscriptionProceduresData, entitiesData);

  // Get raw rooms data from subscription
  const rawRoomsData = roomsSub.data?.rooms;

  // Track last valid rooms data to use when stale events are rejected
  const lastValidRoomsRef = useRef(emptyRooms);

  // Filter stale room events using same two-layer protection as procedures
  const filteredRoomsData = useMemo(() => {
    if (!rawRoomsData || rawRoomsData.length === 0) {
      return lastValidRoomsRef.current;
    }

    // Layer 1: Ignore events within grace period after poll
    if (isWithinPollGracePeriod()) {
      console.log('[HospitalSchedule] Ignoring rooms event within poll grace period');
      return lastValidRoomsRef.current;
    }

    // Layer 2: Check event age using _fetchedAt timestamp from server
    const firstRoom = rawRoomsData[0];
    if (firstRoom?._fetchedAt && isEventStale(firstRoom._fetchedAt)) {
      const eventAge = Date.now() - firstRoom._fetchedAt;
      console.log(
        `[HospitalSchedule] Ignoring stale rooms event (age: ${Math.round(eventAge)}ms, rooms: ${rawRoomsData.length})`
      );
      return lastValidRoomsRef.current;
    }

    // Event is fresh - update last valid data and return
    lastValidRoomsRef.current = rawRoomsData;
    return rawRoomsData;
  }, [rawRoomsData]);

  const roomsArray = useStableSubscriptionData(filteredRoomsData, emptyRooms);

  // Apply same loading timeout logic to rooms subscription
  // If we have rooms data and loading is stuck, use cached data
  const hasReceivedRoomsData = useRef(false);
  const roomsLoadingTimeoutRef = useRef(null);
  const [forceRoomsNotLoading, setForceRoomsNotLoading] = useState(false);

  useEffect(() => {
    if (roomsArray.length > 0) {
      hasReceivedRoomsData.current = true;
      setForceRoomsNotLoading(false);
    }
  }, [roomsArray]);

  useEffect(() => {
    if (roomsSub.loading && hasReceivedRoomsData.current) {
      roomsLoadingTimeoutRef.current = setTimeout(() => {
        console.warn('[HospitalSchedule] Rooms loading timeout reached, forcing display with cached data');
        setForceRoomsNotLoading(true);
      }, 15000);
    } else {
      if (roomsLoadingTimeoutRef.current) {
        clearTimeout(roomsLoadingTimeoutRef.current);
        roomsLoadingTimeoutRef.current = null;
      }
    }
    return () => {
      if (roomsLoadingTimeoutRef.current) {
        clearTimeout(roomsLoadingTimeoutRef.current);
      }
    };
  }, [roomsSub.loading]);

  const roomsLoading = forceRoomsNotLoading ? false : roomsSub.loading;
  const roomsError = roomsSub.error;

  /** Extract parameters from query */
  const isTimelineParam = parsedParams.timeline === 'true';
  const viewParam = parsedParams.view;
  const hospitalId = scope?.hospital?.id;

  // ECST Pattern: Single subscription for all schedule updates
  // - 1 WebSocket connection (scales to unlimited procedures)
  // - Events carry complete procedure data (no refetch needed)
  // - Polling safety net for missed events
  useScheduleUpdates({
    date: formattedDate,
    hospitalId,
    physician: physician ? parseInt(physician, 10) : undefined,
  });

  const currentOR = decodeURIComponent(location.hash).replace('#', '');

  // We can still check or override the timeline if config says so
  const [viewType, setViewType] = useState('overview'); // 'overview' | 'timeline' | 'list'
  const [isViewTransitioning, setIsViewTransitioning] = useState(false);

  const theme = useTheme();
  const [showOverlay, setShowOverlay] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint.md);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint.md);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsMobile]);

  useEffect(() => {
    if (isKiosk) {
      setViewType(config?.isTimeline ? 'timeline' : 'overview');
    } else {
      if (viewParam) {
        setViewType(viewParam);
      } else {
        setViewType(isTimelineParam ? 'timeline' : 'overview');
      }
    }
  }, [isKiosk, config?.isTimeline, isTimelineParam, viewParam]);

  const switchView = newView => {
    // Show loading state immediately
    setIsViewTransitioning(true);

    // Use setTimeout to allow browser to paint the spinner before heavy work
    // Even a 0ms timeout allows the browser to render the loading state first
    setTimeout(() => {
      setViewType(newView);
      const old = queryString.parse(location.search) || {};
      history.replace({
        pathname: location.pathname,
        search: queryString.stringify({
          ...old,
          view: newView,
          timeline: undefined,
        }),
        hash: location.hash,
      });

      // Clear loading state after render completes
      // Use another setTimeout to ensure the new view is painted
      setTimeout(() => setIsViewTransitioning(false), 50);
    }, 0);
  };

  // Extract stable config values to prevent unnecessary recalculations
  const showOrRooms = useMemo(() => getNestedValue('showOrRooms', config), [config]);

  const prevOperationRoomsRef = useRef([]);

  const operationRooms = useMemo(() => {
    if (!roomsArray.length) return emptyArray;

    // Use optimized transform functions (50-70% faster)
    // Shallow-clone rooms before transform since it mutates them (adds .procedures).
    // Without cloning, the transform would mutate objects stored in prevOperationRoomsRef,
    // making isEqual always return true and breaking React.memo on RoomColumn.
    const rooms = getOperationRoomsOptimized({ data: { rooms: roomsArray } }).map(room => ({ ...room }));
    const procedures = getProceduresOptimized({ data: { procedures: proceduresData } });

    const transformed = transformScheduleOperationRoomsOptimized({
      rooms,
      procedures,
    });

    // Combine filters into single pass to reduce iterations
    const filtered = transformed.filter(room => {
      if (showOrRooms && !showOrRooms.includes(room.id)) return false;
      if (shouldFilterOrWithoutId(hospitalId) && !room.id) return false;
      return !room?.isHideScheduleScreen;
    });

    // Single pass: separate by order and sort
    const roomsWithOrder = [];
    const roomsWithoutOrderOR = [];
    const roomsWithoutOrderOther = [];

    for (const room of filtered) {
      if (room?.order !== null) {
        roomsWithOrder.push(room);
      } else {
        const nameUpper = room?.name?.toUpperCase();
        if (nameUpper?.startsWith('OR')) {
          roomsWithoutOrderOR.push(room);
        } else {
          roomsWithoutOrderOther.push(room);
        }
      }
    }

    // Sort only the rooms with order
    roomsWithOrder.sort((a, b) => a.order - b.order);

    // Sort unordered by name
    const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
    const sorted = [...roomsWithOrder, ...roomsWithoutOrderOR.sort(byName), ...roomsWithoutOrderOther.sort(byName)];

    // Stabilize individual room objects: reuse previous references for rooms
    // whose data hasn't changed, so React.memo on RoomColumn can skip re-renders.
    //
    // Safety notes:
    // - Room data arrives exclusively via scheduleRoomsSubscription, which delivers
    //   a fresh object tree on every update (Apollo never mutates nested arrays in-place
    //   for subscription results). So nested fields like staffShifts are always new
    //   references when their content changes.
    // - Procedure data arrives via scheduleProceduresOptimizedSubscription (also fresh
    //   trees). denormalizeProcedure prefers inline subscription values (procedure.patient
    //   etc.) over entity cache lookups, so Apollo cache-only writes to entities do not
    //   introduce shared mutable references into the comparison.
    // - roomEqual does structural comparison, correctly detecting changes in nested
    //   arrays/objects regardless of reference identity.
    // - The shallow clone above (.map(room => ({ ...room }))) exists solely to prevent
    //   transformScheduleOperationRoomsOptimized from mutating objects stored in
    //   prevOperationRoomsRef (it sets room.procedures = [] and pushes into it).
    //   Without the clone, prev and new would be the same object, making roomEqual
    //   always return true and breaking memoization.
    const prevById = new Map();
    for (const room of prevOperationRoomsRef.current) {
      const key = room.id || room.name;
      if (key != null) prevById.set(key, room);
    }

    const stabilized = sorted.map(room => {
      const key = room.id || room.name;
      const prev = key != null ? prevById.get(key) : undefined;
      if (prev && roomEqual(prev, room)) {
        return prev;
      }
      return room;
    });

    prevOperationRoomsRef.current = stabilized;
    return stabilized;
  }, [roomsArray, proceduresData, showOrRooms, hospitalId]);

  /**
   * Decide the core schedule UI
   */
  const anesthesiologistView = isKiosk ? getNestedValue('anesthesiologistSchedule', config) : isAnesthesiologist;

  const ScheduleView = useMemo(
    () =>
      anesthesiologistView
        ? Timeline
        : viewType === 'timeline'
          ? Timeline
          : viewType === 'list'
            ? ScheduleListView
            : Overview,
    [anesthesiologistView, viewType]
  );

  const filteredOperationRooms = useMemo(
    () => (anesthesiologistView ? operationRooms.filter(room => /\bor(?:\s*\d|\b)/i.test(room.name || '')) : []),
    [anesthesiologistView, operationRooms]
  );

  // Retry handler for subscription errors
  const handleSubscriptionRetry = useCallback(() => {
    console.log('[HospitalSchedule] Subscriptions will automatically reconnect when WebSocket reconnects');
    // No manual refetch needed - subscriptions handle reconnection automatically
  }, []);

  const isAdminUser = useMemo(
    () => isSuperAdmin || isGroupAdmin || isAdmin || isAnesthesiologist,
    [isSuperAdmin, isGroupAdmin, isAdmin, isAnesthesiologist]
  );
  const isPowerUser = useMemo(() => isAdminUser || isOperationsManager, [isAdminUser, isOperationsManager]);
  const canUseScheduleViewProcedure = useMemo(
    () => isPowerUser || isPreOPNurse || isFrontDesk,
    [isPowerUser, isPreOPNurse, isFrontDesk]
  );

  const unassignedProcedures = useMemo(
    () => proceduresData.filter(procedure => !procedure.orId && !procedure.or),
    [proceduresData]
  );

  const noProcedures = useMemo(() => {
    if (!proceduresData.length) return true;
    return unassignedProcedures.length === 0 && operationRooms.every(room => room.procedures?.length === 0);
  }, [proceduresData.length, unassignedProcedures.length, operationRooms]);

  const noShifts = useMemo(() => {
    if (!roomsArray.length) return true;
    return roomsArray.every(r => r.staffShifts.length === 0);
  }, [roomsArray]);

  /**
   * For the toggle between day/week/month
   */
  const [view, setView] = useState('day');
  const handleViewChange = useCallback(newView => {
    if (newView !== null) {
      setView(newView);
    }
  }, []);

  const hasMoreThan6ORs = useMemo(() => {
    if (isKiosk) return false;
    return operationRooms.length + (unassignedProcedures.length > 0 ? 1 : 0) > 6;
  }, [operationRooms.length, unassignedProcedures.length, isKiosk]);

  const [hasMoreThan6ORsView, setHasMoreThan6ORsView] = useState(true);
  const toggle6ORViewMode = () => {
    setHasMoreThan6ORsView(prevState => !prevState);
  };

  const formattedMonth = useMemo(() => format(date, 'MMMM YYYY'), [date]);
  const pad = useMemo(() => padWeek(date, getNestedValue('showWeekends', config)), [date, config]);
  const formattedWeekStart = useMemo(() => format(pad?.[0] ?? date, 'MMMM DD'), [pad, date]);
  const formattedWeekEnd = useMemo(() => format(pad?.[pad.length - 1] ?? date, 'MMMM DD'), [pad, date]);

  const classes = hospitalScheduleStyles();
  const isMaxWidth1920 = useMediaQuery('(max-width: 1920px)');
  const isMaxWidth1600 = useMediaQuery('(max-width: 1600px)');
  const isMaxWidth1400 = useMediaQuery('(max-width: 1400px)');

  const [editProcedure, setEditProcedure] = useState(null);
  const [editProcedureMonthly, setEditProcedureMonthly] = useState(null);

  const openProcedureForm = useCallback(
    (procedure, operationRooms, operationRoom, dateArg, timeRange) => {
      history.push({
        pathname: procedure?.id ? `${match.url}/procedure/${procedure?.id}` : `${match.url}/procedure`,
        search: location.search,
        state: { procedure, operationRooms, operationRoom, date: dateArg, timeRange },
      });
    },
    [history, location.search, match.url]
  );

  const onFilterChange = useCallback(
    value => {
      const old = queryString.parse(location.search) || {};
      history.replace({
        pathname: location.pathname,
        search: queryString.stringify({ ...old, ...value }),
        hash: location.hash,
      });
    },
    [history, location.hash, location.pathname, location.search]
  );

  // Remove ?date= from the URL so the screen returns to auto-follow mode.
  // Only defined when there is actually a ?date= to clear; Filters hides
  // the "Today (auto-follow)" button when this is undefined.
  const hasDateInUrl = !!parsedParams.date;
  const onClearDate = useCallback(() => {
    const old = queryString.parse(location.search) || {};
    const { date: _, ...rest } = old;
    history.replace({
      pathname: location.pathname,
      search: queryString.stringify(rest),
      hash: location.hash,
    });
  }, [history, location.hash, location.pathname, location.search]);
  const clearDateIfActive = hasDateInUrl ? onClearDate : undefined;

  const showStaffList = useMemo(() => getNestedValue('showStaffListOnSchedule', config), [config]);
  const showBedNumber = useMemo(() => (isKiosk ? getNestedValue('showBedNumber', config) : true), [isKiosk, config]);
  const showDatePicker = useMemo(() => getNestedValue('showDatePickerOnSchedule', config), [config]);
  const staffCostAccess = useMemo(
    () => props?.session?.account?.metadata?.staffCostAccess,
    [props?.session?.account?.metadata?.staffCostAccess]
  );

  // Use stable rooms array for footer
  const visibleRooms = useMemo(() => roomsArray.filter(e => !e?.isHideScheduleScreen), [roomsArray]);

  const scheduleFooter = useMemo(
    () => (
      <ScheduleFooter
        isFrontDesk={isFrontDesk}
        isPowerUser={isPowerUser}
        isScheduleUser={isScheduleUser}
        hospitalId={hospitalId}
        date={formattedDate}
        rooms={visibleRooms}
        staffId={scheduleUserType === ScheduleUserType.StaffMember ? scheduleUserId : null}
        myProceduresChecked={myProceduresChecked}
        editableStaff={isPowerUser}
        isKiosk={isKiosk}
        isFooter={!showStaffList}
        anesthesiologistView={anesthesiologistView}
        anesthesiologistDate={date}
      />
    ),
    [
      isFrontDesk,
      isPowerUser,
      isScheduleUser,
      hospitalId,
      formattedDate,
      visibleRooms,
      scheduleUserType,
      scheduleUserId,
      myProceduresChecked,
      isKiosk,
      showStaffList,
      anesthesiologistView,
      date,
    ]
  );

  const staffPerRoom = useMemo(() => {
    if (!roomsArray.length || !proceduresData.length) return emptyArray;
    const roomsWithValidStaff = roomsArray.map(room => ({
      ...room,
      staffShifts: room.staffShifts.filter(shift => !!shift.staff?.name),
    }));
    return scheduleStaff(roomsWithValidStaff, getRoomWithStaff(proceduresData));
  }, [roomsArray, proceduresData]);

  const createProcedureSchedule = useMemo(
    () => scope?.hospital?.modules?.createProcedureSchedule,
    [scope?.hospital?.modules?.createProcedureSchedule]
  );
  const showStaffCost = useMemo(
    () => scope?.hospital?.modules?.showStaffCost,
    [scope?.hospital?.modules?.showStaffCost]
  );
  const showCSVUpload = useMemo(
    () => scope?.hospital?.modules?.showCSVUpload,
    [scope?.hospital?.modules?.showCSVUpload]
  );
  const showWeekends = useMemo(() => scope?.hospital?.modules?.showWeekends, [scope?.hospital?.modules?.showWeekends]);
  const secondaryRoomAnesthesiologist = useMemo(
    () => scope?.hospital?.modules?.secondaryRoomAnesthesiologist,
    [scope?.hospital?.modules?.secondaryRoomAnesthesiologist]
  );
  const autoDischarge = useMemo(
    () => scope?.hospital?.modules?.autoDischarge,
    [scope?.hospital?.modules?.autoDischarge]
  );

  // Remove unused state - was causing unnecessary re-renders
  const showGraphs = true;

  return (
    <>
      {isKiosk && <ClientUpdater />}

      {/**
       * If dayIsOver logic is used, HospitalSchedule might pass a "next day" date.
       * Show a note if date is different from the system's current date
       */}
      {dayIsOver && (
        <ToastBar id="forcedDate" variant="warning">
          Note that this screen displays schedule for {format(date, 'dddd')}.
        </ToastBar>
      )}

      {isKiosk && (
        <Helmet>
          <meta name="viewport" content="width=1920, initial-scale=1" />
        </Helmet>
      )}

      <Box
        className={clsx(classes.page, {
          [classes.kiosk]: isKiosk,
          [classes.hdtv]: isMaxWidth1920,
          [classes.kioskMaxWidth1600]: isKiosk && isMaxWidth1600,
          [classes.kioskMaxWidth1400]: isKiosk && isMaxWidth1400,
        })}
      >
        {/**
         * 1) If user is power user or relevant staff => editing header
         * 2) If normal schedule user => simpler
         * 3) If read only => show bare
         */}
        {isPowerUser || isPreOPNurse || isNursing || isFrontDesk || isMonitoringEngineer ? (
          <>
            <Box className={classes.editingHeader}>
              <Box className={classes.mainActions}>
                <Button
                  component={RouterLink}
                  color="primary"
                  variant="text"
                  size="medium"
                  to={isAdmin ? '/' : `/su/${hospitalId}`}
                  startIcon={<ArrowBack />}
                >
                  {` Back${isOperationsManager ? '' : isPowerUser ? ' to admin' : ''}`}
                </Button>

                <Box alignSelf="normal" mr={1}>
                  <Divider orientation="vertical" />
                </Box>

                <Typography component="h1" variant="h4" align="center">
                  {scope?.hospital?.name}
                </Typography>
              </Box>

              <div className={classes.actions}>
                {/** Show CSV upload if relevant */}
                {!createProcedureSchedule && !isMonitoringEngineer && !isAnesthesiologist && showCSVUpload && (
                  <ScheduleUploadButton
                    history={history}
                    location={location}
                    formats={formats}
                    className="vanishing"
                    color="primary"
                  />
                )}

                <Box alignSelf="normal" mx={1}>
                  <Divider orientation="vertical" />
                </Box>

                {createProcedureSchedule && viewType === 'timeline' && (
                  <ToggleOverlay showOverlay={showOverlay} setShowOverlay={setShowOverlay} />
                )}

                {!isAnesthesiologist && (
                  <div>
                    <Select
                      value={viewType}
                      size="small"
                      options={[
                        { value: 'overview', label: 'Overview' },
                        { value: 'timeline', label: 'Timeline' },
                        { value: 'list', label: 'Compact List' },
                      ]}
                      onChange={switchView}
                    />
                  </div>
                )}

                {hasMoreThan6ORs && (viewType === 'timeline' || isAnesthesiologist) && (
                  <div>
                    <Button variant="outlined" onClick={toggle6ORViewMode}>
                      {hasMoreThan6ORsView ? 'Scroll View' : 'Full View'}
                    </Button>
                  </div>
                )}

                {!isAnesthesiologist && (
                  <Select
                    value={view}
                    size="small"
                    options={[
                      { value: 'day', label: 'Day' },
                      { value: 'week', label: 'Week' },
                      { value: 'month', label: 'Month' },
                    ]}
                    onChange={handleViewChange}
                  />
                )}
                <Box display="flex" alignItems="center">
                  <Filters
                    color={theme.palette.background.default}
                    textColor={theme.palette.text.primary}
                    date={date}
                    physician={physician}
                    onChange={onFilterChange}
                    onClearDate={clearDateIfActive}
                    withoutPhysician={isAnesthesiologist}
                  />
                </Box>
                {!isAnesthesiologist && (
                  <>
                    <Box alignSelf="normal" ml={1}>
                      <Divider orientation="vertical" />
                    </Box>

                    <Box display="flex" alignItems="center">
                      {roomsArray.length > 0 && <StaffShiftCopyButton date={date} rooms={roomsArray} />}

                      {showStaffCost && ((isAdminUser && staffCostAccess) || isSuperAdmin) && (
                        <StaffingCostButton match={match} date={formattedDate} />
                      )}

                      {(isPowerUser || isFrontDesk || isPreOPNurse) && (
                        <PDFScheduleButton date={date} staffPerRoom={staffPerRoom} operationRooms={operationRooms} />
                      )}

                      {!isFrontDesk && !isPreOPNurse && !isMonitoringEngineer && (
                        <SendNotificationDropdown date={date} />
                      )}
                      <ScheduleNotificationDialog />
                    </Box>
                  </>
                )}
              </div>
            </Box>

            {!isFrontDesk && <MultipleSelectionBox />}

            {!!editProcedure && (
              <ProcedureEditor
                date={date}
                operationRooms={operationRooms.map(r => r.name)}
                procedure={editProcedure}
                onDone={() => setEditProcedure(null)}
                onClose={() => setEditProcedure(null)}
                fullWindow={isMobile}
                isAnesthesiologist={isAnesthesiologist}
              />
            )}
            {!!editProcedureMonthly && (
              <ProcedureEditor
                date={date}
                operationRooms={operationRooms.map(r => r.name)}
                procedure={editProcedureMonthly}
                onDone={() => setEditProcedureMonthly(null)}
                onClose={() => setEditProcedureMonthly(null)}
                fullWindow={isMobile}
                isAnesthesiologist={isAnesthesiologist}
              />
            )}
          </>
        ) : isScheduleUser ? (
          <Box className={classes.editingHeader}>
            <Box display="flex" alignItems="center" ml={1}>
              <Select
                value={viewType}
                size="small"
                options={[
                  { value: 'overview', label: 'Overview' },
                  { value: 'timeline', label: 'Timeline' },
                  { value: 'list', label: 'Compact List' },
                ]}
                onChange={switchView}
              />
            </Box>
            <HospitalName>{scope?.hospital?.name}</HospitalName>
            <Row>
              {hasScheduleAccessAllRight && (
                <CheckboxInput
                  label="My procedures"
                  name="myProcedure"
                  value={myProceduresChecked}
                  onChange={setMyProceduresChecked}
                  layoutProps={{ style: { marginRight: theme.spacing(1) } }}
                />
              )}
              <Filters date={date} withoutPhysician onChange={onFilterChange} onClearDate={clearDateIfActive} />
            </Row>
          </Box>
        ) : showDatePicker ? (
          <Header>
            <HospitalInfo date={date} onDateChange={onFilterChange} onClearDate={clearDateIfActive} />
          </Header>
        ) : (
          <Header>
            <HospitalInfo />
          </Header>
        )}

        {/**
         * Loading or Error states
         * Show loader while:
         * - Entities are being fetched (physicians, anesthesiologists, patients)
         * - Rooms subscription is loading
         * - Procedures subscription is loading
         */}
        <SubscriptionErrorHandler
          loading={entitiesLoading || roomsLoading || proceduresLoading}
          error={roomsError || proceduresError}
          onRetry={handleSubscriptionRetry}
          maxAutoRetries={3}
          initialDelay={5}
          maxDelay={300}
          showErrorDetails={import.meta.env.DEV}
          isKioskMode={isKiosk}
        >
          <>
            {/* View transition loading overlay */}
            {isViewTransitioning && (
              <AbsoluteSpinner
                style={{
                  zIndex: 9999,
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                }}
                size="60px"
              />
            )}

            {noProcedures && noShifts && view === 'day' ? (
              <EmptySchedule
                date={date}
                timelineView={viewType === 'timeline'}
                physician={physician}
                isKiosk={isKiosk}
                isScheduleUser={isScheduleUser}
                myProceduresOnly={myProceduresChecked}
                hospitalId={hospitalId}
                currentOR={currentOR}
                isFrontDesk={isFrontDesk}
                isPowerUser={isPowerUser}
                editableStaff={isPowerUser}
                operationRooms={operationRooms}
                setEditProcedure={setEditProcedure}
                unassignedProcedures={unassignedProcedures}
                showBedNumber={showBedNumber}
                scheduleFooter={showStaffList ? scheduleFooter : null}
                history={history}
                match={match}
                canUseScheduleViewProcedure={canUseScheduleViewProcedure}
                openProcedureForm={openProcedureForm}
                showOverlay={showOverlay}
              />
            ) : (
              <>
                {isMobile && <MobileORNavigation rooms={operationRooms} />}
                {view === 'day' ? (
                  <ScheduleView
                    date={date}
                    hospitalId={hospitalId}
                    isKiosk={isKiosk}
                    isMobile={isMobile}
                    currentOR={currentOR}
                    canUseScheduleViewProcedure={canUseScheduleViewProcedure}
                    isFrontDesk={isFrontDesk}
                    isPowerUser={isPowerUser}
                    editableStaff={isPowerUser}
                    operationRooms={anesthesiologistView ? filteredOperationRooms : operationRooms}
                    setEditProcedure={setEditProcedure}
                    unassignedProcedures={unassignedProcedures}
                    showBedNumber={showBedNumber}
                    scheduleStaffList={showStaffList ? scheduleFooter : null}
                    history={history}
                    match={match}
                    openProcedureForm={openProcedureForm}
                    showOverlay={showOverlay}
                    isPreOPNurse={isPreOPNurse}
                    isNursing={isNursing}
                    hasMoreThan6ORsView={hasMoreThan6ORsView}
                    showGraphs={showGraphs}
                    isAnesthesiologistSchedule={anesthesiologistView}
                    secondaryRoomAnesthesiologist={secondaryRoomAnesthesiologist}
                    autoDischarge={autoDischarge}
                  />
                ) : view === 'month' ? (
                  <ScrollableContainer>
                    <MonthContainer>
                      <h2>{formattedMonth}</h2>
                    </MonthContainer>
                    <MonthlySchedule
                      date={date}
                      physician={physician}
                      setEditProcedure={setEditProcedure}
                      setEditProcedureMonthly={setEditProcedureMonthly}
                      openProcedureForm={openProcedureForm}
                      canUseScheduleViewProcedure={canUseScheduleViewProcedure}
                      showWeekends={showWeekends}
                    />
                  </ScrollableContainer>
                ) : (
                  <ScrollableContainer>
                    <MonthContainer>
                      <h2>
                        {formattedWeekStart} - {formattedWeekEnd}
                      </h2>
                    </MonthContainer>
                    <WeeklySchedule
                      date={date}
                      physician={physician}
                      setEditProcedureMonthly={setEditProcedureMonthly}
                      openProcedureForm={openProcedureForm}
                      canUseScheduleViewProcedure={canUseScheduleViewProcedure}
                      showWeekends={showWeekends}
                    />
                  </ScrollableContainer>
                )}
                {!showStaffList && view === 'day' && scheduleFooter}
              </>
            )}
          </>
        </SubscriptionErrorHandler>
      </Box>
      {/** ROUTES for schedule procedure forms */}
      <Route
        path={`${match.url}/procedure`}
        render={routeProps => {
          const isCorrectPath = routeProps?.location?.pathname === `${match.url}/procedure`;
          return (
            isCorrectPath && (
              <ScheduleProcedureForm
                operationRooms={routeProps?.location?.state?.operationRooms}
                date={routeProps?.location?.state?.date}
                operationRoom={routeProps?.location?.state?.operationRoom}
                timeRange={routeProps?.location?.state?.timeRange}
                handleClose={() => routeProps.history.goBack()}
                showGraphs={showGraphs}
                theme={anesthesiologistView ? 'gray' : 'blue'}
              />
            )
          );
        }}
      />
      <Route
        path={`${match.url}/procedure/:procedureId`}
        render={routeProps => {
          const procedureId = parseInt(routeProps?.match?.params?.procedureId, 10);
          const isCorrectPath = routeProps?.location?.pathname === `${match.url}/procedure/${procedureId}`;
          return (
            isCorrectPath && (
              <ScheduleProcedureForm
                procedureId={procedureId}
                procedure={routeProps?.location?.state?.procedure}
                operationRooms={routeProps?.location?.state?.operationRooms}
                date={routeProps?.location?.state?.date}
                operationRoom={routeProps?.location?.state?.operationRoom}
                timeRange={routeProps?.location?.state?.timeRange}
                handleClose={() => routeProps.history.goBack()}
                showGraphs={showGraphs}
                theme={anesthesiologistView ? 'gray' : 'blue'}
              />
            )
          );
        }}
      />
    </>
  );
};

// Stable empty array reference to prevent unnecessary re-renders
const emptyArray = [];
const emptyRooms = [];

// Object.hasOwn polyfill for browsers older than Chrome 93 / Firefox 92 / Safari 15.4
const hasOwn = Object.hasOwn || ((obj, key) => Object.prototype.hasOwnProperty.call(obj, key));

/**
 * Fast structural equality check for room objects produced by transformScheduleOperationRooms.
 *
 * Replaces lodash/isEqual on this hot path. Both objects come from the same
 * subscription → transform → shallow-clone pipeline, so:
 * - Key sets are always identical (no undefined-vs-missing-key issue in practice,
 *   but guarded with Object.hasOwn defensively).
 * - Property insertion order is deterministic, so JSON.stringify produces
 *   consistent output for nested arrays/objects.
 * - JSON.stringify only runs for nested values whose references differ,
 *   which is typically 1 room out of ~12 per subscription update.
 */
const roomEqual = (a, b) => {
  const keysA = Object.keys(a);
  if (keysA.length !== Object.keys(b).length) return false;
  for (let i = 0; i < keysA.length; i++) {
    const k = keysA[i];
    if (!hasOwn(b, k)) return false;
    const va = a[k];
    const vb = b[k];
    if (va === vb) continue;
    if (typeof va !== 'object' || va === null || typeof vb !== 'object' || vb === null) return false;
    if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
  }
  return true;
};

function useDayIsOver({
  formattedDate,
  physician,
  currentDate,
  isPowerUser,
  isScheduleUser,
  useDateOverride,
  useForcedDate,
  isPreOPNurse,
}) {
  /** Subscription: Procedures => fetch ALL */
  const { data } = useSubscription(proceduresDayOverSubscription, {
    variables: { date: formattedDate, physician },
  });

  const proceduresData = data?.procedures || emptyArray;

  const computedDayIsOver = useMemo(() => {
    if (!proceduresData.length) return false;

    // Every procedure must belong to the requested date. The `date` field is
    // resolved server-side in the hospital's timezone, so the comparison is
    // timezone-correct. During date transitions Apollo may briefly replay
    // cached data from the previous date; rejecting those prevents stale
    // dayIsOver from re-enabling after the staleness guard below clears it.
    if (!proceduresData.every(p => p.date === formattedDate)) return false;

    const proceduresAreOver = proceduresData.every(procedure => {
      if (procedure.isCanceled) return true;
      const fullyRecovered =
        procedure.patient?.events && (procedure.patient.events.recoveryAt || procedure.patient.events.dischargedAt);
      if (fullyRecovered) return true;
      return !procedure.patient && isAfter(currentDate, addMinutes(procedure.startTime, procedure.duration));
    });

    return (
      !isPowerUser &&
      !isScheduleUser &&
      !useDateOverride &&
      !useForcedDate &&
      !isPreOPNurse &&
      proceduresData.length > 0 &&
      proceduresAreOver
    );
  }, [
    proceduresData,
    formattedDate,
    currentDate,
    isPowerUser,
    isScheduleUser,
    useDateOverride,
    useForcedDate,
    isPreOPNurse,
  ]);

  // Track which date dayIsOver was computed for.
  // When the date changes (midnight transition), dayIsOver from the previous date
  // is stale and must not be applied — otherwise finalDate briefly jumps to the
  // wrong date (day-after-tomorrow), causing rapid subscription churn that can
  // leave the screen stuck on yesterday's schedule.
  const [dayIsOver, setDayIsOver] = useState(false);
  const confirmedDateRef = useRef(formattedDate);

  // Synchronous staleness guard: if date changed, reset before children render.
  // React will re-render this component with dayIsOver=false before rendering children,
  // so the Schedule component never sees the stale dayIsOver=true with the new date.
  if (confirmedDateRef.current !== formattedDate) {
    confirmedDateRef.current = formattedDate;
    if (dayIsOver) {
      setDayIsOver(false);
    }
  }

  useEffect(() => {
    setDayIsOver(prev => (prev === computedDayIsOver ? prev : computedDayIsOver));
  }, [computedDayIsOver]);

  return dayIsOver;
}

const HospitalSchedule = props => {
  const {
    location,
    isSuperAdmin,
    isGroupAdmin,
    isMonitoringEngineer,
    isAdmin,
    isFrontDesk,
    isOperationsManager,
    isScheduleUser,
    isPreOPNurse,
    date: forcedDate, // Possibly forced externally
  } = props;

  const hospitalTimezone = props.scope?.hospital?.timezone?.id;
  const currentDate = useClock(hospitalTimezone);

  /** Param logic: parse once here */
  const parsedParams = useMemo(() => queryString.parse(location.search), [location.search]);
  const physician = useMemo(
    () => (parsedParams.physician ? parseInt(parsedParams.physician, 10) : null),
    [parsedParams.physician]
  );

  // If there's a date in query string, parse it.
  // Memoized to avoid creating a new Date object on every render,
  // which would defeat the useMemo below.
  const dateOverride = useMemo(() => parse((parsedParams.date || '') + 'T00:00:00'), [parsedParams.date]);
  const hasDateInUrl = !isNaN(dateOverride.getTime());

  // For kiosk screens, only honor ?date= when the user has a visible way to
  // control it (date picker, admin controls, etc.). A stale or orphaned ?date=
  // parameter — from a previous config, a shared URL, or someone selecting
  // "today" in the picker — can silently lock a kiosk with no way to clear it.
  const isPowerUser = useMemo(
    () => isSuperAdmin || isGroupAdmin || isMonitoringEngineer || isAdmin || isFrontDesk || isOperationsManager,
    [isSuperAdmin, isGroupAdmin, isMonitoringEngineer, isAdmin, isFrontDesk, isOperationsManager]
  );

  const showDatePicker = useMemo(() => getNestedValue('showDatePickerOnSchedule', props.config), [props.config]);
  const hasDateControls = isPowerUser || isScheduleUser || isPreOPNurse || props.isNursing || showDatePicker;
  const useDateOverride = hasDateInUrl && (!props.isKiosk || hasDateControls);

  /**
   * Decide the final date
   * - If forcedDate is valid, use that
   * - Else if dateOverride is valid, use that
   * - Else fallback to currentDate
   */
  const date = useMemo(() => {
    if (forcedDate && !isNaN(forcedDate.getTime())) return forcedDate;
    if (useDateOverride) return dateOverride;
    return currentDate;
  }, [forcedDate, useDateOverride, dateOverride, currentDate]);

  const formattedDate = useMemo(() => format(date, 'YYYY-MM-DD'), [date]);

  const useForcedDate = !!(forcedDate && !isNaN(forcedDate.getTime()));

  const dayIsOver = useDayIsOver({
    formattedDate,
    physician,
    currentDate,
    isPowerUser,
    isScheduleUser,
    useDateOverride,
    useForcedDate,
    isPreOPNurse,
  });

  /** If day is over => show next day, else show the date we derived above */
  const finalDate = dayIsOver ? startOfDay(addDays(date, 1)) : date;

  return (
    <MultipleStaffShiftSelectionContextProvider date={formattedDate}>
      <Schedule {...props} parsedParams={parsedParams} physician={physician} date={finalDate} dayIsOver={dayIsOver} />
    </MultipleStaffShiftSelectionContextProvider>
  );
};

export const hospitalScheduleStyles = makeStyles(theme => ({
  page: {
    flex: 1,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    display: 'flex',
    flexDirection: 'column',
    fontSize: '1.075rem',
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      fontSize: '0.875rem',
      WebkitOverflowScrolling: 'touch',
    },
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1),
    [responsive.md.andSmaller]: {
      overflow: 'hidden',
      flexWrap: 'wrap',
      flexFlow: 'column',
    },
  },
  hdtv: {
    fontSize: '1rem',
  },
  kiosk: {
    [theme.breakpoints.down('lg')]: {
      fontSize: '1rem',
    },
  },
  kioskMaxWidth1600: {
    fontSize: '0.8125rem',
  },
  kioskMaxWidth1400: {
    fontSize: '0.6875rem',
  },
  editingHeader: {
    display: 'flex',
    padding: '0.875em 1em',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
      // overflow: 'hidden',
      flexFlow: 'column',
      alignItems: 'stretch',
    },
    '& > div': {
      [theme.breakpoints.down('md')]: {
        flex: 'initial',
      },
    },
  },
  mainActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      '& .vanishing': {
        display: 'none',
      },
    },
  },
  divider: {
    alignSelf: 'stretch',
  },
  toggleCalendar: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}));

export default HospitalSchedule;
